import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import {
  createOfferCampaign,
  getStoreOfferCampaign,
  listOfferCampaignSellables,
  listStoreOfferCampaigns,
  mapOfferCampaignError,
  softDeleteOfferCampaign,
  updateOfferCampaign,
} from '../services/offer-campaign.service';
import type { SupabaseClient } from '../supabase/client';
import type {
  CreateOfferCampaignInput,
  DeleteOfferCampaignInput,
  OfferCampaignErrorKey,
  OfferCampaignSellableOption,
  OfferCampaignWithItems,
  UpdateOfferCampaignInput,
} from '../types/offer-campaign';

export const offerCampaignKeys = {
  all: ['offer-campaigns'] as const,
  list: (storeId: string) => ['offer-campaigns', 'list', storeId] as const,
  detail: (storeId: string, campaignId: string) =>
    ['offer-campaigns', 'detail', storeId, campaignId] as const,
  sellables: (storeId: string, search: string, categoryId: string | null) =>
    ['offer-campaigns', 'sellables', storeId, search, categoryId] as const,
  publicLive: (storeSlug: string) => ['public-offer-campaigns', 'live', storeSlug] as const,
  publicPage: (storeSlug: string, campaignSlug: string) =>
    ['public-offer-campaigns', 'page', storeSlug, campaignSlug] as const,
};

export interface UseStoreOfferCampaignsResult extends Pick<
  UseQueryResult<OfferCampaignWithItems[]>,
  'data' | 'isLoading' | 'isError' | 'error' | 'refetch'
> {
  readonly campaigns: OfferCampaignWithItems[];
  readonly errorKey: OfferCampaignErrorKey | null;
}

export function useStoreOfferCampaigns(
  client: SupabaseClient,
  storeId: string | null,
): UseStoreOfferCampaignsResult {
  const q = useQuery({
    queryKey: storeId ? offerCampaignKeys.list(storeId) : offerCampaignKeys.all,
    queryFn: () => listStoreOfferCampaigns(client, storeId ?? ''),
    enabled: !!storeId,
    staleTime: 60_000,
  });

  return {
    data: q.data,
    campaigns: q.data ?? [],
    isLoading: q.isLoading,
    isError: q.isError,
    error: q.error,
    refetch: q.refetch,
    errorKey: q.error ? mapOfferCampaignError(q.error) : null,
  };
}

export function useStoreOfferCampaign(
  client: SupabaseClient,
  args: { readonly storeId: string | null; readonly campaignId: string | null },
): UseQueryResult<OfferCampaignWithItems | null> {
  return useQuery({
    queryKey:
      args.storeId && args.campaignId
        ? offerCampaignKeys.detail(args.storeId, args.campaignId)
        : offerCampaignKeys.all,
    queryFn: () =>
      getStoreOfferCampaign(client, {
        store_id: args.storeId ?? '',
        campaign_id: args.campaignId ?? '',
      }),
    enabled: !!args.storeId && !!args.campaignId,
    staleTime: 60_000,
  });
}

export function useOfferCampaignSellables(
  client: SupabaseClient,
  args: {
    readonly storeId: string | null;
    readonly search: string;
    readonly categoryId?: string | null;
  },
): UseQueryResult<OfferCampaignSellableOption[]> {
  const search = args.search.trim();
  const categoryId = args.categoryId ?? null;
  return useQuery({
    queryKey: args.storeId
      ? offerCampaignKeys.sellables(args.storeId, search, categoryId)
      : offerCampaignKeys.all,
    queryFn: () =>
      listOfferCampaignSellables(client, {
        store_id: args.storeId ?? '',
        search,
        category_id: categoryId,
      }),
    enabled: !!args.storeId,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}

export function useCreateOfferCampaign(
  client: SupabaseClient,
): UseMutationResult<OfferCampaignWithItems, unknown, CreateOfferCampaignInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input) => createOfferCampaign(client, input),
    onSuccess: (campaign, input) => {
      void queryClient.invalidateQueries({ queryKey: offerCampaignKeys.list(input.store_id) });
      void queryClient.invalidateQueries({
        queryKey: offerCampaignKeys.detail(campaign.store_id, campaign.id),
      });
      void queryClient.invalidateQueries({ queryKey: ['store-home-merchandising'] });
      void queryClient.invalidateQueries({ queryKey: ['public-offer-campaigns'] });
    },
  });
}

export function useUpdateOfferCampaign(
  client: SupabaseClient,
): UseMutationResult<OfferCampaignWithItems, unknown, UpdateOfferCampaignInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input) => updateOfferCampaign(client, input),
    onSuccess: (campaign) => {
      void queryClient.invalidateQueries({ queryKey: offerCampaignKeys.list(campaign.store_id) });
      void queryClient.invalidateQueries({
        queryKey: offerCampaignKeys.detail(campaign.store_id, campaign.id),
      });
      void queryClient.invalidateQueries({ queryKey: ['store-home-merchandising'] });
      void queryClient.invalidateQueries({ queryKey: ['public-offer-campaigns'] });
    },
  });
}

export function useDeleteOfferCampaign(
  client: SupabaseClient,
): UseMutationResult<void, unknown, DeleteOfferCampaignInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input) => softDeleteOfferCampaign(client, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: offerCampaignKeys.all });
      void queryClient.invalidateQueries({ queryKey: ['store-home-merchandising'] });
      void queryClient.invalidateQueries({ queryKey: ['public-offer-campaigns'] });
    },
  });
}
